<template>
  <ul class="tag-list" :class="'tag-list--' + tone">
    <li v-for="tag in tags" :key="tag">{{ tag }}</li>
  </ul>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* A row of short labels that are not links: a card's feature tags, a project's
   categories, the stack a site was built on.

   Two of these already existed and had drifted, which is what a fourth copy
   would have finished: `.services-tags` sat on the dark service cards and
   `.studio-badges` on the mobile page's light cards, and they disagreed on the
   type size, the weight, the padding and whether there is a border. They are the
   same object, so the ground chooses the colour and the page chooses only the
   tone.

   A chip answers the pointer with its ground and nothing else, and that is the
   one thing about this component that had to be argued rather than inherited. A
   tag is a statement about the thing beside it, not a control, and a chip that
   lifts under the pointer teaches it to expect a link that never arrives. So
   there is no lift here, no shadow and no scale: the chip answers with one step
   of its ground, on the colour duration, and it reads as the label settling
   rather than as a button waking up. It is the same restraint the ruled list
   keeps when it refuses its rows a fill for the same reason.

   The step is each ground's own, because the two grounds define a chip
   differently: on light the tint is the edge, and on dark the hairline is. */
.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    border-radius: $r-pill;
    /* One canonical chip. The two that came together here disagreed on the size,
       the weight and the padding, so the stronger of the two is the one that
       stays and both pages take the repaint. */
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    padding: 0.3rem 0.7rem;
    transition: background $dur-quick $ease, border-color $dur-quick $ease;
  }
}

/* On a light ground the chip is a soft tint of the brand blue with no border:
   the tint itself is the edge. The pointer steps that tint to the same value
   the outline button's own hover fills with, so the two agree. */
.tag-list--light li {
  background: $tint-primary-soft;
  color: $primary;

  &:hover {
    background: $tint-primary;
  }
}

/* On a dark ground a tint of the same blue disappears, so the chip is a raised
   fill with its own hairline, the way every other object on a dark band is. The
   pointer lights that hairline to the ground's accent, which is the colour the
   site already uses to mark the emphasised thing on ink. */
.tag-list--dark li {
  background: $on-dark-fill-strong;
  border: 1px solid $on-dark-line-strong;
  color: $on-dark-strong;

  &:hover {
    border-color: $dark-accent;
  }
}
</style>

<script>
export default {
  props: {
    /* Plain strings. A tag is a label, and the caller passes it translated. */
    tags: {
      type: Array,
      required: true
    },
    /* light | dark: which ground the row is sitting on. */
    tone: {
      type: String,
      default: 'light'
    }
  }
};
</script>
